import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import { ageFromDateOfBirth, toBirthDate } from '@/lib/patient/age';
import {
  getPatientDemographics,
  savePatientDemographics,
  type Gender,
  type PatientDemographics,
} from '@/lib/firebase/queries/patientDemographics';

interface PatientDemographicsCardProps {
  userId: string;
  readOnly?: boolean;
  /** Called with the loaded and the saved values, so the exam can classify BMI by age (#90). */
  onChange?: (value: PatientDemographics) => void;
}

/**
 * Gender and age of the patient at the top of the exam and questionnaire.
 * When either is missing the examiner fills them in here; they are saved to
 * the patient's profile, not to the exam, so they are asked only once.
 */
export function PatientDemographicsCard({ userId, readOnly = false, onChange }: PatientDemographicsCardProps) {
  const { t, dir } = useLanguage();
  const [value, setValue] = useState<PatientDemographics | null>(null);
  const [editing, setEditing] = useState(false);
  const [gender, setGender] = useState<Gender | ''>('');
  const [dob, setDob] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setValue(null);
    getPatientDemographics(userId)
      .then((v) => {
        if (cancelled) return;
        setValue(v);
        onChange?.(v);
        setGender(v.gender ?? '');
        setDob(v.dateOfBirth ? format(v.dateOfBirth, 'yyyy-MM-dd') : '');
        setEditing(!readOnly && (!v.gender || !v.dateOfBirth));
      })
      .catch((error) => {
        console.error('Error loading patient demographics:', error);
        if (!cancelled) setValue({ gender: null, dateOfBirth: null });
      });
    return () => { cancelled = true; };
  }, [userId, readOnly]);

  const birth = toBirthDate(dob || null);
  const draftAge = ageFromDateOfBirth(birth);
  const canSave = !!gender && draftAge !== null;

  const save = async () => {
    if (!canSave || !birth) return;
    setSaving(true);
    try {
      await savePatientDemographics(userId, { gender: gender as Gender, dateOfBirth: birth });
      setValue({ gender: gender as Gender, dateOfBirth: birth });
      onChange?.({ gender: gender as Gender, dateOfBirth: birth });
      setEditing(false);
      toast.success(t('dashboard.examiner.patient.saved'));
    } catch (error) {
      console.error('Error saving patient demographics:', error);
      toast.error(t('dashboard.examiner.patient.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!value) {
    return <div className="h-10 rounded-md border bg-muted/30 animate-pulse" />;
  }

  const age = ageFromDateOfBirth(value.dateOfBirth);

  if (!editing) {
    return (
      <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm" dir={dir}>
        <div className="flex flex-wrap items-center gap-4">
          <span>
            <span className="text-muted-foreground">{t('dashboard.examiner.patient.gender')}: </span>
            {value.gender ? t(`dashboard.examiner.patient.${value.gender}`) : '—'}
          </span>
          <span>
            <span className="text-muted-foreground">{t('dashboard.examiner.patient.age')}: </span>
            {age !== null ? t('dashboard.examiner.patient.years', { count: age }) : '—'}
          </span>
        </div>
        {!readOnly && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)} aria-label={t('dashboard.examiner.patient.edit')}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border px-3 py-3 space-y-3" dir={dir}>
      <p className="text-sm text-muted-foreground">{t('dashboard.examiner.patient.missingHint')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="space-y-1">
          <Label>{t('dashboard.examiner.patient.gender')}</Label>
          <Select value={gender} onValueChange={(v) => setGender(v as Gender)}>
            <SelectTrigger>
              <SelectValue placeholder={t('dashboard.examiner.patient.chooseGender')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t('dashboard.examiner.patient.male')}</SelectItem>
              <SelectItem value="female">{t('dashboard.examiner.patient.female')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>
            {t('dashboard.examiner.patient.dateOfBirth')}
            {draftAge !== null && (
              <span className="text-muted-foreground"> ({t('dashboard.examiner.patient.years', { count: draftAge })})</span>
            )}
          </Label>
          <Input type="date" value={dob} max={format(new Date(), 'yyyy-MM-dd')} onChange={(e) => setDob(e.target.value)} dir="ltr" />
        </div>
        <Button type="button" onClick={save} disabled={!canSave || saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('dashboard.examiner.patient.save')}
        </Button>
      </div>
    </div>
  );
}
